// ---------------------------------------------------------------------------
// Anchor-balance passes: Word repairs a document whose comment ranges are
// unbalanced, and silently drops unpaired bookmarks. Both arise when the
// paragraph carrying one side of a pair is rebuilt without its markers.
// ---------------------------------------------------------------------------

export function balanceCommentAnchors(xml: string): string {
  const collect = (re: RegExp) => { const s = new Set<string>(); for (const m of xml.matchAll(re)) s.add(m[1]); return s; };
  const starts = collect(/<w:commentRangeStart\b[^>]*w:id="(\d+)"/g);
  if (starts.size === 0) {
    // stray ends with no start at all — drop them
    return xml.replace(/<w:commentRangeEnd\b[^>]*\/>/g, (m) => {
      const id = (m.match(/w:id="(\d+)"/) || [])[1];
      return id && starts.has(id) ? m : '';
    });
  }
  const ends = collect(/<w:commentRangeEnd\b[^>]*w:id="(\d+)"/g);
  const refs = collect(/<w:commentReference\b[^>]*w:id="(\d+)"/g);
  // stray ends (no matching start) — drop
  xml = xml.replace(/<w:commentRangeEnd\b[^>]*w:id="(\d+)"[^>]*\/>/g, (m, id) => (starts.has(id) ? m : ''));
  // starts missing their end and/or reference — close the range right at the
  // start and re-anchor the balloon so the comment survives instead of making
  // Word repair the file.
  const doneEnd = new Set<string>(), doneRef = new Set<string>();
  xml = xml.replace(/<w:commentRangeStart\b[^>]*w:id="(\d+)"[^>]*\/>/g, (m, id, offset, whole) => {
    let out = m;
    if (!ends.has(id) && !doneEnd.has(id)) { doneEnd.add(id); out += `<w:commentRangeEnd w:id="${id}"/>`; }
    if (!refs.has(id) && !doneRef.has(id)) {
      // a <w:r> is only legal inside a paragraph — range markers can also sit at
      // tbl/tr level (comment spanning rows); skip the synthetic run there.
      const before = (whole as string).slice(0, offset as number);
      const inParagraph = before.lastIndexOf('<w:p ') > before.lastIndexOf('</w:p>') || before.lastIndexOf('<w:p>') > before.lastIndexOf('</w:p>');
      if (inParagraph) { doneRef.add(id); out += `<w:r><w:commentReference w:id="${id}"/></w:r>`; }
    }
    return out;
  });
  return xml;
}

export function balanceBookmarks(xml: string): string {
  const starts = new Set<string>();
  for (const m of xml.matchAll(/<w:bookmarkStart\b[^>]*w:id="(-?\d+)"/g)) starts.add(m[1]);
  const ends = new Set<string>();
  for (const m of xml.matchAll(/<w:bookmarkEnd\b[^>]*w:id="(-?\d+)"/g)) ends.add(m[1]);
  // drop ends with no start
  xml = xml.replace(/<w:bookmarkEnd\b[^>]*w:id="(-?\d+)"[^>]*\/>/g, (m, id) => (starts.has(id) ? m : ''));
  // close starts with no end immediately (Word keeps the bookmark, zero-width)
  const done = new Set<string>();
  xml = xml.replace(/<w:bookmarkStart\b[^>]*w:id="(-?\d+)"[^>]*\/>/g, (m, id) => {
    if (ends.has(id) || done.has(id)) return m;
    done.add(id);
    return m + `<w:bookmarkEnd w:id="${id}"/>`;
  });
  return xml;
}
